import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import PlanDocument from "@/components/PlanDocument";
import RecallReminderModal from "@/components/RecallReminderModal";
import { usePatients } from "@/components/PatientPicker";
import { PatientSearchSelect } from "@/components/PatientPicker";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Eye, Printer, Filter, Loader2, CalendarClock, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClinikoSendsList, PlanSends } from "@/components/ClinikoSends";

function fmt(iso) { try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } }
const STATUS = { draft: "bg-slate-100 text-slate-600", finalized: "bg-blue-100 text-blue-700", pushed: "bg-emerald-100 text-emerald-700" };

export default function PlanHistory() {
  const [plans, setPlans] = useState([]);
  const [patient, setPatient] = useState("all");
  const [mode, setMode] = useState("all");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [recallFor, setRecallFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const pending = useRef({});

  const commitDelete = async (pl) => {
    delete pending.current[pl.id];
    try {
      const { data } = await api.delete(`/plans/${pl.id}`);
      if (data.reminders_stopped) toast.success(`${data.reminders_stopped} pending reminder(s) stopped`);
    } catch {
      setPlans((ps) => (ps.some((p) => p.id === pl.id) ? ps : [pl, ...ps]));
      toast.error("Could not delete plan — it has been restored");
    }
  };

  useEffect(() => () => {
    Object.values(pending.current).forEach(({ timer, pl }) => { clearTimeout(timer); api.delete(`/plans/${pl.id}`).catch(() => {}); });
  }, []);

  const deletePlan = () => {
    const pl = toDelete;
    setToDelete(null);
    setPlans((ps) => ps.filter((p) => p.id !== pl.id));
    const timer = setTimeout(() => commitDelete(pl), 6000);
    pending.current[pl.id] = { timer, pl };
    toast("Plan deleted", {
      duration: 6000,
      action: { label: "Undo", onClick: () => {
        clearTimeout(timer);
        delete pending.current[pl.id];
        setPlans((ps) => [...ps, pl].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
        toast.success("Plan restored");
      } },
    });
  };

  const exportPdf = async (pl) => {
    setExporting(true);
    try {
      const res = await api.get(`/plans/${pl.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(pl.patient_name || "plan").replace(/\s+/g, "_")}_plan.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF — retry"); }
    finally { setExporting(false); }
  };

  const load = useCallback(() => {
    const q = {};
    if (patient !== "all") q.cliniko_id = patient;
    if (mode !== "all") q.mode = mode;
    if (status !== "all") q.status = status;
    api.get("/plans", { params: q }).then((r) => setPlans(r.data)).catch(() => {});
  }, [patient, mode, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout title="Plan History" subtitle="All saved rehabilitation, posture & maintenance plans">
      <Tabs defaultValue="plans">
      <TabsList className="mb-4">
        <TabsTrigger value="plans" data-testid="history-tab-plans">Plans</TabsTrigger>
        <TabsTrigger value="sends" data-testid="history-tab-sends">Sent to Cliniko</TabsTrigger>
      </TabsList>
      <TabsContent value="sends"><ClinikoSendsList /></TabsContent>
      <TabsContent value="plans">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-slate-400" />
        <div className="w-60"><PatientSearchSelect value={patient} onChange={setPatient} allowAll testid="history-filter-patient" placeholder="All patients" /></div>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="w-44 bg-white" data-testid="history-filter-mode"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All types</SelectItem><SelectItem value="rehabilitation">Rehabilitation</SelectItem><SelectItem value="posture">Posture</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid="history-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All status</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="finalized">Finalized</SelectItem><SelectItem value="pushed">Pushed</SelectItem></SelectContent>
        </Select>
      </div>

      {plans.length === 0 ? (
        <div className="grid h-64 place-items-center rounded-2xl border border-dashed border-slate-300 text-slate-400">
          <div className="text-center"><ClipboardList className="mx-auto mb-2 h-7 w-7" /> No saved plans match these filters</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((pl) => (
            <div key={pl.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`plan-card-${pl.id}`}>
              <div className="flex items-start justify-between">
                <div><div className="font-heading font-bold text-slate-900">{pl.patient_name}</div><div className="font-mono text-[11px] text-slate-400">{pl.cliniko_id}</div></div>
                <div className="flex gap-1">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${STATUS[pl.status] || STATUS.draft}`}>{pl.status}</span>
                  <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold capitalize text-teal-700">{pl.mode}</span>
                </div>
              </div>
              <div className="mt-2 text-sm text-slate-600">{pl.diagnosis}</div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                <span>{pl.overview?.total_duration_weeks}w · {pl.overview?.recommended_sessions} sessions</span>
                <span>{fmt(pl.created_at)}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setView(pl)} data-testid={`plan-view-${pl.id}`} className="flex-1 border-teal-200 text-teal-700 hover:bg-teal-50"><Eye className="mr-1.5 h-3.5 w-3.5" /> View plan</Button>
                <Button size="sm" variant="outline" onClick={() => setToDelete(pl)} data-testid={`plan-delete-${pl.id}`} aria-label="Delete plan" className="border-red-200 px-2.5 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      </TabsContent>
      </Tabs>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid="plan-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this plan?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.patient_name} · {toDelete?.mode} · {toDelete && fmt(toDelete.created_at)}. This can't be undone. Unsent reminder emails for this plan will be stopped; anything already in Cliniko stays.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plan-delete-cancel">Keep plan</AlertDialogCancel>
            <AlertDialogAction onClick={deletePlan} className="bg-red-600 hover:bg-red-700" data-testid="plan-delete-confirm">Delete plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto bg-white">
          <div className="flex justify-end gap-2 no-print">
            <Button size="sm" variant="outline" onClick={() => setRecallFor(view)} data-testid="history-recall-btn" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              <CalendarClock className="mr-1.5 h-4 w-4" /> Recall
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportPdf(view)} disabled={exporting} data-testid="history-export-pdf-btn">
              {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Printer className="mr-1.5 h-4 w-4" />} Download PDF
            </Button>
          </div>
          <PlanSends planId={view?.id} />
          <PlanDocument plan={view} />
        </DialogContent>
      </Dialog>
      <RecallReminderModal open={!!recallFor} onOpenChange={(o) => !o && setRecallFor(null)} plan={recallFor} />
    </Layout>
  );
}
